/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { parseValue } from '@ui/utils/format';
import type { LiquidityStrategyDepositToken } from '@ui/utils/strategy';
import {
  LiquidityStrategyTokenDict,
  useDepositTokenBalances,
  useSharesFromTokenAssets,
  useTokenSdk,
  useVaultBalance,
  useVaultSdk,
} from '@ui/utils/strategy';
import type { EthersError } from '@veno-app/sdk';
import { currentWallet } from '@veno-app/wallet';
import { useMemo } from 'react';

import { useDepositDrawer } from './useDepositDrawer';

export enum LiquidityStrategyStep {
  default,
  pending,
  success,
  failed,
}

export const useDeposit = () => {
  const token = useDepositDrawer((s) => s.token);
  const amountStr = useDepositDrawer((s) => s.amountStr);
  const client = useQueryClient();

  const account = currentWallet.useAccount();
  const vaultSdk = useVaultSdk(token);
  const tokenSdk = useTokenSdk(token);
  const refetchVault = useRefetchVault(token);

  const amountBn = useMemo(() => {
    return parseValue(amountStr, 18);
  }, [amountStr]);

  return useMutation(async () => {
    if (!vaultSdk || !tokenSdk || !account) {
      return;
    }

    useDepositDrawer.setState({
      step: LiquidityStrategyStep.pending,
    });

    try {
      const allowance = await tokenSdk.allowance(
        account,
        vaultSdk.contractAddress,
      );

      if (allowance.lt(amountBn)) {
        useDepositDrawer.setState({
          pendingNSteps: 2,
          pendingCurrentStep: 1,
        });

        const approveTransaction = await tokenSdk.approve(
          vaultSdk.contractAddress,
          amountBn,
        );

        await approveTransaction.txReceiptPromise;

        useDepositDrawer.setState({
          pendingCurrentStep: 2,
        });
      }

      const transaction = await vaultSdk.deposit(amountBn, account);

      const receipt = await transaction.txReceiptPromise;

      refetchVault();

      useDepositDrawer.setState({
        step: LiquidityStrategyStep.success,
        receivedAmount: vaultSdk.getDepositReceivedAmount(receipt, account),
      });
      client.invalidateQueries(['useVaultExchangeRate']);
      client.invalidateQueries(['useVaultBalance']);
    } catch (err) {
      useDepositDrawer.setState({
        step: LiquidityStrategyStep.failed,
        errorCode: (err as EthersError).code,
        errorMessage: (err as EthersError).message || '',
      });

      throw err;
    }
  });
};

export const useWithdraw = () => {
  const client = useQueryClient();
  const token = useDepositDrawer((s) => s.token);
  const amountStr = useDepositDrawer((s) => s.amountStr);

  const account = currentWallet.useAccount();
  const vaultSdk = useVaultSdk(token);
  const refetchVault = useRefetchVault(token);
  const tokenInfo = LiquidityStrategyTokenDict[token];

  const withdrawSharesBn = useSharesFromTokenAssets(
    token,
    parseValue(amountStr, tokenInfo.decimals),
  );

  return useMutation(async () => {
    if (!vaultSdk || !account || !withdrawSharesBn) {
      return;
    }

    useDepositDrawer.setState({
      step: LiquidityStrategyStep.pending,
    });

    try {
      if (
        vaultSdk.shouldApproveWhenWithdraw &&
        vaultSdk.approveWhenWithdraw &&
        (await vaultSdk.shouldApproveWhenWithdraw(account, withdrawSharesBn))
      ) {
        useDepositDrawer.setState({
          pendingNSteps: 2,
          pendingCurrentStep: 1,
        });

        const approveTransaction = await vaultSdk.approveWhenWithdraw(
          withdrawSharesBn,
        );

        await approveTransaction.txReceiptPromise;

        useDepositDrawer.setState({
          pendingCurrentStep: 2,
        });
      }

      const transaction = await vaultSdk.withdraw(withdrawSharesBn, account);

      const receipt = await transaction.txReceiptPromise;

      refetchVault();

      useDepositDrawer.setState({
        step: LiquidityStrategyStep.success,
        receivedAmount: vaultSdk.getWithdrawReceivedAmount(receipt, account),
      });
      client.invalidateQueries(['useVaultExchangeRate']);
      client.invalidateQueries(['useVaultBalance']);
    } catch (err) {
      useDepositDrawer.setState({
        step: LiquidityStrategyStep.failed,
        errorCode: (err as EthersError).code,
        errorMessage: (err as EthersError).message || '',
      });

      throw err;
    }
  });
};

const useRefetchVault = (token: LiquidityStrategyDepositToken) => {
  const balanceRet = useDepositTokenBalances({
    enabled: false,
  })[token];
  const vaultBalanceRet = useVaultBalance(token, {
    enabled: false,
  });

  return () => {
    balanceRet.refetch();
    vaultBalanceRet.refetch();
  };
};
